import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";

// ─────────────────────────────────────────────────────────────────────────────
// WHISTLE CHECK §4 (24 Jul 2026) — FIND ME A MATCH. The empty-feed liquidity
// gap: OPEN PLAY's empty state only offered HOST MATCH. This is the
// "SUBSCRIBE FOR NEXT MATCH" secondary flow — one active subscription per
// player, resolved by the FIRST joinable card that appears (§5.4b RULE 2
// recent-opponent filtered — never notified into a rematch they can't take),
// delivered by magic-link SMS (opt-in) and/or live feed update, and always
// terminated: claimed elsewhere, hosted their own match, or a config'd
// timeout with a graceful host-instead nudge. Never a silent, unbounded wait.
// ─────────────────────────────────────────────────────────────────────────────

export const FindMatchStatus = z.enum(["waiting", "fulfilled", "timeout", "cancelled"]);
export type FindMatchStatus = z.infer<typeof FindMatchStatus>;

export const FindMatchSubscribeRequest = z.object({
  night_code: z.string().min(1),
  sms_opt_in: z.boolean(),
});
export type FindMatchSubscribeRequest = z.infer<typeof FindMatchSubscribeRequest>;

// GET /find-match/status — polled by the "FINDING YOU A MATCH" screen.
export const FindMatchStatusView = z.object({
  status: FindMatchStatus,
  sms_opt_in: z.boolean(),
  subscribed_at: IsoTimestamp,
  /** Present once status is "fulfilled" — the card to deep-link into. */
  match_id: MatchId.nullable(),
  opponent_gamertag: z.string().nullable(),
  /** The config'd deadline (subscribed_at + moments.find_match_timeout_ms) —
   *  the client can render its own countdown without polling the clock. */
  timeout_at: IsoTimestamp,
});
export type FindMatchStatusView = z.infer<typeof FindMatchStatusView>;

// Internal (match-engine → messaging), NOT a player-facing contract — the
// magic-link SMS trigger. Reuses the existing magic_link_match template
// ("{opponent} is ready...") verbatim; the "opponent" IS the new card's host.
export const FindMatchSmsTrigger = z.object({
  player_id: PlayerId,
  match_id: MatchId,
  night_id: NightId,
  opponent_gamertag: z.string(),
});
export type FindMatchSmsTrigger = z.infer<typeof FindMatchSmsTrigger>;
